import { COUNTRY_MAP } from '@/lib/countries';

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** Compact form for axis ticks and share labels, where width is scarce. */
export function fmtCompact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${Math.round(n / 1000)}k`;
  if (abs >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return fmtInt(n);
}

export function fmtMoney(dollars: number): string {
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtPct(v: number, digits = 2): string {
  return `${v.toFixed(digits)}%`;
}

/** Signed percentage for period-over-period deltas. null = grew from zero. */
export function fmtDelta(v: number | null): string {
  if (v === null) return 'new';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(Math.abs(v) < 10 ? 1 : 0)}%`;
}

export function flagOf(code: string): string {
  if (!code || code === '??' || code === 'XX') return '🌍';
  const c = COUNTRY_MAP[code];
  if (c) return c.flag;
  if (!/^[A-Z]{2}$/.test(code)) return '🌍';
  return String.fromCodePoint(...[...code].map((ch) => 127397 + ch.charCodeAt(0)));
}

export function countryName(code: string): string {
  if (code === '??') return 'Unknown';
  return COUNTRY_MAP[code]?.name ?? code;
}

// One colour per metric, used for that metric's sparkline and its trend area.
// Only ever one series is plotted at a time, so these never have to separate
// from each other inside a single chart — but the three that do sit side by side
// in the funnel (indigo / green / amber) were checked for CVD separation.
export interface MetricPaint { light: string; dark: string }

export const METRIC_PAINT: Record<string, MetricPaint> = {
  clicks:      { light: '#635BFF', dark: '#8F85FF' },
  uniq_clicks: { light: '#4F46E5', dark: '#A5B4FC' },
  regs:        { light: '#10B981', dark: '#34D399' },
  deps:        { light: '#F59E0B', dark: '#FBBF24' },
  revenue:     { light: '#EC4899', dark: '#F472B6' },
  cr_dep:      { light: '#10B981', dark: '#34D399' },
  epc:         { light: '#EC4899', dark: '#F472B6' },
};

export function paint(metric: string, theme: 'light' | 'dark'): string {
  const p = METRIC_PAINT[metric] ?? METRIC_PAINT.clicks!;
  return theme === 'dark' ? p.dark : p.light;
}

/** dd MMM, the axis label format the rest of the dashboard uses. */
export function shortDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function downloadCsv(filename: string, header: string[], rows: (string | number)[][]): void {
  const escape = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [header, ...rows].map((r) => r.map(escape).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([`﻿${body}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
